/**
 * "A new version is available — Reload."
 *
 * The app is a single page: once loaded, a tab keeps running the code it
 * started with, however many deploys go out behind it. Someone who leaves
 * Cornerstone open all day would otherwise report bugs that were fixed hours
 * ago. So the page re-reads index.html when the tab regains focus (and every
 * ten minutes), compares the bundle it names with the one this tab loaded, and
 * offers a reload when they differ. Never a forced reload: that would throw
 * away a half-typed form.
 */

import { useEffect, useState } from 'react'

const BUNDLE = /\/assets\/index-[\w-]+\.js/

function loadedBundle(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
  return script?.getAttribute('src')?.match(BUNDLE)?.[0] ?? null
}

export function UpdateNotice() {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    const mine = loadedBundle()
    // The dev server has no hashed bundle to compare.
    if (!mine) return
    let last = 0
    async function check() {
      if (Date.now() - last < 60_000) return
      last = Date.now()
      try {
        const html = await (await fetch('/', { cache: 'no-store' })).text()
        const live = html.match(BUNDLE)?.[0]
        if (live && live !== mine) setStale(true)
      } catch {
        // Offline or a blip: try again on the next focus.
      }
    }
    const timer = window.setInterval(() => void check(), 10 * 60_000)
    window.addEventListener('focus', check)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', check)
    }
  }, [])

  if (!stale) return null
  return (
    <p className="update-notice" role="status">
      <span>A new version of Cornerstone is available.</span>
      <button type="button" className="update-notice__reload" onClick={() => window.location.reload()}>
        Reload
      </button>
    </p>
  )
}
